import type { ReactNode } from 'react';
import { Download, Loader2, RefreshCw } from 'lucide-react';
import type { ScanAgentUnavailableReason } from './scan-agent';
import { explainUnavailable } from './scan-agent-reasons';
import { useScanDownload } from './scan-download';
import { ScanIntro } from './ScanIntro';
import { ScanInstallSteps } from './ScanInstallSteps';

export interface ScanUnavailableProps {
  reason: ScanAgentUnavailableReason;
  /** What the agent itself said, when it answered and still could not list a scanner. */
  problem: string | null;
  onRetry: () => void;
}

/**
 * Why scanning is not possible yet, as a picture, a headline and one thing to do. Paige Scan not
 * installed is the common first visit: three small steps and the download, and the sheet keeps
 * looking by itself (useScanAgent's recheck), so the quiet "Checking" line replaces Try again and the
 * scanner list appears once the installer has run.
 */
export function ScanUnavailable({ reason, problem, onRetry }: ScanUnavailableProps): ReactNode {
  const said = explainUnavailable(reason);
  const { url } = useScanDownload();
  return (
    <div className="paige-scan-setup" role="status">
      <ScanIntro title={said.title} lede={said.detail} />
      {said.install && <ScanInstallSteps />}
      {problem && <p className="paige-scan-note">{problem}</p>}
      {said.install ? (
        <a className="paige-scan-cta" href={url}>
          <Download size={16} aria-hidden="true" />
          Download Paige Scan
        </a>
      ) : (
        !said.waits && (
          <button type="button" className="paige-scan-cta is-quiet" onClick={onRetry}>
            <RefreshCw size={14} aria-hidden="true" />
            Try again
          </button>
        )
      )}
      {said.waits && (
        <p className="paige-scan-watching">
          <Loader2 size={13} className="paige-spin" aria-hidden="true" />
          {said.install ? 'Checking… choose Allow if the browser asks.' : 'Checking…'}
        </p>
      )}
      {!said.install && (
        <a className="paige-scan-quiet-link" href={url}>
          Download Paige Scan
        </a>
      )}
    </div>
  );
}
